import { FormEvent, useState } from 'react'
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'

export default function SuperAdminSetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [success, setSuccess] = useState(false)

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  }

  const isValid = Object.values(passwordChecks).every(Boolean) && password === repeatPassword

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!isValid) return

    try {
      setLoading(true)
      setErrorText('')

      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })

      if (!response.ok) {
        const body = await response.json()
        setErrorText(body?.message?.content ?? 'Something went wrong.')
        return
      }

      setSuccess(true)
      setTimeout(() => navigate('/super-admin/login'), 2000)
    } catch {
      setErrorText('Network error.')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Container className='py-5'>
        <Row className='justify-content-center'>
          <Col md={6} lg={4}>
            <p className='text-danger'>Invalid or missing token.</p>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container className='py-5'>
      <Row className='justify-content-center'>
        <Col md={6} lg={4}>
          <Card>
            <Card.Body>
              <h4 className='mb-3'>Set Password</h4>

              {success ? (
                <p className='text-success'>Password set successfully. Redirecting to login...</p>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className='mb-3'>
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type='password'
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete='new-password'
                      autoFocus
                    />
                    <ul className='list-unstyled mt-2 mb-0' style={{ fontSize: '0.85rem' }}>
                      <li style={{ color: passwordChecks.length ? 'green' : 'inherit' }}>
                        <i
                          className={
                            passwordChecks.length ? 'bi bi-check-circle-fill' : 'bi bi-circle'
                          }
                        />{' '}
                        Min. 8 characters
                      </li>
                      <li
                        style={{
                          color:
                            passwordChecks.uppercase && passwordChecks.lowercase
                              ? 'green'
                              : 'inherit',
                        }}
                      >
                        <i
                          className={
                            passwordChecks.uppercase && passwordChecks.lowercase
                              ? 'bi bi-check-circle-fill'
                              : 'bi bi-circle'
                          }
                        />{' '}
                        Upper and lowercase letters
                      </li>
                      <li style={{ color: passwordChecks.number ? 'green' : 'inherit' }}>
                        <i
                          className={
                            passwordChecks.number ? 'bi bi-check-circle-fill' : 'bi bi-circle'
                          }
                        />{' '}
                        At least one number
                      </li>
                    </ul>
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>Repeat Password</Form.Label>
                    <Form.Control
                      type='password'
                      value={repeatPassword}
                      onChange={e => setRepeatPassword(e.target.value)}
                      autoComplete='new-password'
                      isInvalid={!!repeatPassword && password !== repeatPassword}
                    />
                    <Form.Control.Feedback type='invalid'>
                      Passwords do not match.
                    </Form.Control.Feedback>
                  </Form.Group>

                  {!!errorText && <p className='text-danger mb-3'>{errorText}</p>}

                  <Button type='submit' disabled={loading || !isValid}>
                    {loading ? 'Saving...' : 'Set Password'}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}
